import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import {Typography, Space, Tag, Card, message} from "antd";
import { CopyOutlined } from '@ant-design/icons';
import versionClient from "../../client/VersionClient";

const { Paragraph } = Typography;

// Значения подставляет сборка: CRA вшивает в бандл только переменные с префиксом
// REACT_APP_, а их выставляет amplify.yml из того, что даёт Amplify (AWS_COMMIT_ID,
// AWS_BRANCH). Локально их нет — отсюда прочерк вместо хеша.
const FRONTEND_BUILD = {
    commit: process.env.REACT_APP_COMMIT,
    branch: process.env.REACT_APP_BRANCH,
    buildTime: process.env.REACT_APP_BUILD_TIME,
};

const LOCAL_BUILD = "local build";
const MONO = { fontFamily: "monospace", wordBreak: "break-all" };

const DebugPage = () => {
    const [accessToken, setAccessToken] = useState("");
    const [decodedToken, setDecodedToken] = useState(null);
    const [scopes, setScopes] = useState([]);
    const [theme, setTheme] = useState("light");
    const [preferredLanguages, setPreferredLanguages] = useState([]);
    const [browserInfo, setBrowserInfo] = useState({});
    const [connectionInfo, setConnectionInfo] = useState({});
    const [screenInfo, setScreenInfo] = useState({});
    const [backendBuild, setBackendBuild] = useState(null);
    const [backendBuildError, setBackendBuildError] = useState(null);

    useEffect(() => {
        versionClient.getVersion()
            .then(setBackendBuild)
            .catch((error) => {
                console.error("Failed to load backend version:", error);
                setBackendBuildError(error.message);
            });
    }, []);

    useEffect(() => {
        const findAccessToken = () => {
            for (const key in localStorage) {
                if (key.includes(".accessToken")) {
                    return localStorage.getItem(key);
                }
            }
            return null;
        };

        const token = findAccessToken();
        if (token) {
            setAccessToken(token);
            try {
                const decoded = jwtDecode(token);
                setDecodedToken(decoded);

                const parsedScopes = decoded.scope ? decoded.scope.split(" ") : [];
                setScopes(parsedScopes);
            } catch (err) {
                console.error("Failed to decode token:", err);
            }
        } else {
            console.warn("Access token not found in localStorage.");
        }

        const darkThemeMq = window.matchMedia("(prefers-color-scheme: dark)");
        setTheme(darkThemeMq.matches ? "dark" : "light");

        const themeListener = (e) => {
            setTheme(e.matches ? "dark" : "light");
        };

        darkThemeMq.addEventListener("change", themeListener);

        setPreferredLanguages(navigator.languages || []);

        setBrowserInfo({
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            cookieEnabled: navigator.cookieEnabled !== undefined ? navigator.cookieEnabled.toString() : "No Data",
        });

        if (navigator.connection) {
            setConnectionInfo({
                type: navigator.connection.effectiveType,
                downlink: `${navigator.connection.downlink} Mbps`,
                rtt: `${navigator.connection.rtt} ms`,
            });
        }

        setScreenInfo({
            resolution: `${window.screen.width} x ${window.screen.height}`,
            availableResolution: `${window.screen.availWidth} x ${window.screen.availHeight}`,
            colorDepth: `${window.screen.colorDepth} bits`,
            pixelRatio: window.devicePixelRatio,
        });

        return () => {
            darkThemeMq.removeEventListener("change", themeListener);
        };
    }, []);

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp * 1000);
        return date.toLocaleString();
    };

    const getTimeLeft = (timestamp) => {
        const now = Math.floor(Date.now() / 1000);
        const diff = timestamp - now;
        if (diff <= 0) return "Expired";
        const minutes = Math.floor(diff / 60);
        const seconds = diff % 60;
        return `${minutes}m ${seconds}s`;
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(accessToken).then(() => {
            message.success('Token copied to clipboard');
        });
    };

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                padding: "20px",
                maxWidth: "800px",
                margin: "0 auto",
                boxSizing: "border-box",
            }}
        >
            <Card
                title="Version"
                style={{ width: "100%", marginBottom: "20px" }}
            >
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                    <Typography.Text strong>Frontend</Typography.Text>
                    <Typography.Text>
                        <strong>commit:</strong>{" "}
                        <span style={MONO}>{FRONTEND_BUILD.commit || LOCAL_BUILD}</span>
                    </Typography.Text>
                    <Typography.Text>
                        <strong>branch:</strong> {FRONTEND_BUILD.branch || LOCAL_BUILD}
                    </Typography.Text>
                    <Typography.Text>
                        <strong>built:</strong> {FRONTEND_BUILD.buildTime || LOCAL_BUILD}
                    </Typography.Text>

                    {/* Text — инлайновый span, отступ на нём не сработал бы без display */}
                    <Typography.Text strong style={{ display: "block", marginTop: "12px" }}>
                        Backend
                    </Typography.Text>
                    {backendBuildError && (
                        <Typography.Text type="danger">{backendBuildError}</Typography.Text>
                    )}
                    {!backendBuildError && !backendBuild && (
                        <Typography.Text type="secondary">Loading...</Typography.Text>
                    )}
                    {backendBuild && (
                        <>
                            <Typography.Text>
                                <strong>version:</strong> {backendBuild.version}
                            </Typography.Text>
                            <Typography.Text>
                                <strong>commit:</strong>{" "}
                                <span style={MONO}>{backendBuild.commit}</span>
                            </Typography.Text>
                            <Typography.Text>
                                <strong>built:</strong> {backendBuild.buildTime}
                            </Typography.Text>
                        </>
                    )}
                </Space>
            </Card>

            {accessToken ? (
                <Card
                    title={
                        <Space>
                            <CopyOutlined
                                onClick={copyToClipboard}
                                style={{ cursor: "pointer", color: "gray" }}
                            />
                            Access Token
                        </Space>
                    }
                    style={{ width: "100%", marginBottom: "20px" }}
                >
                    <Paragraph style={{ wordBreak: "break-word" }}>{accessToken}</Paragraph>
                </Card>
            ) : (
                <Typography.Text type="danger">No Access Token Found</Typography.Text>
            )}

            {scopes.length > 0 && (
                <Card
                    title="User Scopes"
                    style={{ width: "100%", marginBottom: "20px" }}
                >
                    <Space wrap>
                        {scopes.map((scope) => (
                            <Tag color={scope === "ADMIN" ? "red" : "blue"} key={scope}>
                                {scope}
                            </Tag>
                        ))}
                    </Space>
                </Card>
            )}

            {decodedToken && (
                <Card
                    title="Decoded Token"
                    style={{ width: "100%", marginBottom: "20px" }}
                >
                    <Space direction="vertical" size="small">
                        {Object.entries(decodedToken).map(([key, value]) => {
                            if (["auth_time", "exp", "iat"].includes(key)) {
                                return (
                                    <Typography.Text key={key}>
                                        <strong>{key}:</strong> {value}
                                        <Tag color="blue" style={{ marginLeft: "10px" }}>
                                            {formatTimestamp(value)}
                                        </Tag>
                                        {key === "exp" && (
                                            <Tag color="orange" style={{ marginLeft: "10px" }}>
                                                {getTimeLeft(value)}
                                            </Tag>
                                        )}
                                    </Typography.Text>
                                );
                            }
                            return (
                                <Typography.Text key={key}>
                                    <strong>{key}:</strong> {value.toString()}
                                </Typography.Text>
                            );
                        })}
                    </Space>
                </Card>
            )}

            <Card
                title="System Info"
                style={{ width: "100%", marginBottom: "20px" }}
            >
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                    {/* Current Theme */}
                    <Typography.Text>
                        <strong>Current Theme:</strong> {theme}
                    </Typography.Text>

                    {/* Preferred Languages */}
                    <Typography.Text>
                        <strong>Preferred Languages:</strong> {JSON.stringify(preferredLanguages)}
                        <Space wrap style={{ marginLeft: "10px" }}>
                            {preferredLanguages.map((lang) => (
                                <Tag color="geekblue" key={lang}>{lang}</Tag>
                            ))}
                        </Space>
                    </Typography.Text>

                    {/* Browser Info */}
                    {Object.entries(browserInfo).map(([key, value]) => (
                        <Typography.Text key={key}>
                            <strong>{key.replace(/([A-Z])/g, " $1")}:</strong> {value}
                        </Typography.Text>
                    ))}

                    {/* Connection Info */}
                    {Object.entries(connectionInfo).length > 0 && (
                        <>
                            <Typography.Text>
                                <strong>Connection Type:</strong> {connectionInfo.type}
                            </Typography.Text>
                            <Typography.Text>
                                <strong>Downlink Speed:</strong> {connectionInfo.downlink}
                            </Typography.Text>
                            <Typography.Text>
                                <strong>RTT:</strong> {connectionInfo.rtt}
                            </Typography.Text>
                        </>
                    )}

                    {/* Screen Info */}
                    {Object.entries(screenInfo).map(([key, value]) => (
                        <Typography.Text key={key}>
                            <strong>{key.replace(/([A-Z])/g, " $1")}:</strong> {value}
                        </Typography.Text>
                    ))}
                </Space>
            </Card>
        </div>
    );
};

export default DebugPage;
